import React from 'react';
import { useHistory } from '@react-router';
import qs from 'query-string';
import { useCountdownGiftCountController } from '@apps/registry/admin/components/CountdownGiftCount/CountdownGiftCount.controller';
import { LoadingState, EditState } from './components';
import { useCashFundProgress, useRegistryGiftCounts, useGenerateInitialMessage } from './RegistryGiftCounts.controller';
import { useTranslation } from '@shared/core';
import { Page, useRegistryGiftCountsTelemetry } from './telemetry';
import { useRoutePaths } from '@apps/registry/admin/Registry.routes';
import { useCatalogContext } from '../Catalog';
import { CardContainer } from './RegistryGiftAdvisor.styles';
import { Box } from '@withjoy/joykit';
import { messageKeys } from './RegistryAdvisor.utils';
import AdvisorHeader from './components/AdvisorHeader/AdvisorHeader';
import AdvisorMetrics from './components/AdvisorMetrics/AdvisorMetrics';
import AdvisorPriceRanges from './components/AdvisorPriceRanges/AdvisorPriceRanges';

interface RegistryAdvisorMobileProps {
  eventHandle: string;
  eventId: string;
  page: Page;
  onActionComplete?: () => void;
}

export const RegistryAdvisorMobile: React.FC<RegistryAdvisorMobileProps> = ({ eventHandle, eventId, page, onActionComplete }) => {
  const { everything, purchased, countDownValue, handleOnGiftTypeClick } = useCountdownGiftCountController({ eventHandle });
  const {
    getResultsLoading,
    initialDataLoading,
    formik,
    handleOnEditClick,
    isEditing,
    completionPercent,
    targetGiftCount,
    getPriceRangeTitleAndLink,
    priceRanges,
    isInZeroState,
    formattedCompletionPercent
  } = useRegistryGiftCounts({ eventId, page, eventHandle });

  const { cashFundTotalCount, cashFundCompletionPercent } = useCashFundProgress();
  const { t } = useTranslation('sharedComponents');
  const { giftsAvailable, giftCount, cashFundCount, giftPriceBalance, reservedAndPurchased, cashFundCompleted, giftsCompleted, shopNow } = t('registryGiftAdvisorV2');
  const history = useHistory();
  const paths = useRoutePaths();
  const { priceRangeClick } = useRegistryGiftCountsTelemetry();
  const { removeSelectedCategory } = useCatalogContext();

  const initialMessage = useGenerateInitialMessage({
    giftsCompletionPercent: completionPercent,
    cashFundCompletionPercent,
    guestCount: formik.values.guestCount || formik.initialValues.guestCount,
    targetedGiftCount: targetGiftCount
  });

  const handlePriceRangeClick = (title: string, priceRangeQuery: string) => {
    priceRangeClick(title, page, 'Gift Count / Price Point Guide');
    removeSelectedCategory();
    history.push({
      pathname: `${paths.catalog.path}/recommended`,
      search: qs.stringify({ pf: `priceRange=${priceRangeQuery}`, showFilters: true })
    });
    onActionComplete?.();
  };

  return (
    <CardContainer>
      <AdvisorHeader
        isEditing={isEditing}
        countDownValue={countDownValue}
        guestCount={formik.values.guestCount}
        initialGuestCount={formik.initialValues.guestCount}
        handleOnEditClick={handleOnEditClick}
        isMobile={true}
      />
      {initialDataLoading ? (
        <Box padding={6}>
          <LoadingState />
        </Box>
      ) : isEditing || isInZeroState ? (
        <Box padding={6}>
          <EditState formik={formik} />
        </Box>
      ) : (
        <>
          <AdvisorMetrics
            everything={everything}
            purchased={purchased}
            getResultsLoading={getResultsLoading}
            handleOnGiftTypeClick={handleOnGiftTypeClick}
            giftsAvailable={giftsAvailable}
            reservedAndPurchased={reservedAndPurchased}
            completionPercent={completionPercent}
            formattedCompletionPercent={formattedCompletionPercent}
            giftsCompleted={giftsCompleted}
            giftCount={giftCount}
            targetGiftCount={targetGiftCount}
            cashFundCompletionPercent={cashFundCompletionPercent}
            cashFundTotalCount={cashFundTotalCount}
            cashFundCompleted={cashFundCompleted}
            cashFundCount={cashFundCount}
            messageKeys={messageKeys}
            initialMessage={initialMessage}
            isMobile={true}
            onActionComplete={onActionComplete}
          />
          <AdvisorPriceRanges
            getResultsLoading={getResultsLoading}
            giftPriceBalance={giftPriceBalance}
            priceRanges={priceRanges}
            getPriceRangeTitleAndLink={getPriceRangeTitleAndLink}
            targetGiftCount={targetGiftCount}
            shopNow={shopNow}
            onPriceRangeClick={handlePriceRangeClick}
            page={page}
            isMobile={true}
          />
        </>
      )}
    </CardContainer>
  );
};

export default RegistryAdvisorMobile;
